import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  Dialog,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import CountContext from '../../context/countContext';
import useVoucher from '../../hooks/useVoucher';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import CloseIcon from '@mui/icons-material/Close';

function statusLabel(status) {
  const s = String(status || '').trim();
  if (s === 'pending') return 'offen';
  if (s === 'approved') return 'freigegeben';
  if (s === 'rejected') return 'abgelehnt';
  return s || '-';
}

function statusChip(status) {
  const s = String(status || '');
  if (s === 'pending') return <Chip size='small' label={statusLabel(s)} color='warning' />;
  if (s === 'approved') return <Chip size='small' label={statusLabel(s)} color='success' />;
  if (s === 'rejected') return <Chip size='small' label={statusLabel(s)} color='default' />;
  return <Chip size='small' label={statusLabel(s)} />;
}

function formatUser(user) {
  if (!user) return '-';
  const parts = [user.firstName, user.lastName].filter(Boolean);
  return parts.length ? parts.join(' ') : user.username || '-';
}

function formatEuro(value) {
  return Number(value || 0).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function monthlyBudgetErrorMessage(error) {
  const budgetData = error?.data?.data || error?.data || {};
  return `Monatskontingent überschritten: verfügbar ${formatEuro(
    budgetData.remaining
  )} €, angefragt ${formatEuro(budgetData.requested)} €.`;
}

function GiftedBalanceVoucherApprovals() {
  const authCtx = useContext(AuthContext);
  const countCtx = useContext(CountContext);
  const {
    getGiftedBalanceVoucherRequests,
    approveGiftedBalanceVoucherRequest,
    rejectGiftedBalanceVoucherRequest,
  } = useVoucher();

  const [status, setStatus] = useState('pending');
  const [isLoading, setIsLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [busyRequestIds, setBusyRequestIds] = useState(() => new Set());
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectDialogRequest, setRejectDialogRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const busySet = useMemo(() => busyRequestIds, [busyRequestIds]);

  const markBusy = (id, next) => {
    setBusyRequestIds(prev => {
      const copy = new Set(prev);
      if (next) copy.add(id);
      else copy.delete(id);
      return copy;
    });
  };

  const load = async () => {
    setIsLoading(true);
    try {
      const res = await getGiftedBalanceVoucherRequests(status);
      setRequests(Array.isArray(res?.results) ? res.results : []);
    } catch (error) {
      toast.error('Fehler beim Laden der Freigabeanfragen');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [status]);

  const handleApprove = async requestId => {
    markBusy(requestId, true);
    try {
      const res = await approveGiftedBalanceVoucherRequest(requestId);
      const createdCount =
        Number(res?.count || (Array.isArray(res?.vouchers) ? res.vouchers.length : 0)) || 0;
      toast.success(
        createdCount > 1
          ? `Freigegeben: ${createdCount} Gutscheine erstellt`
          : `Freigegeben: ${res?.voucherCode || 'Gutschein erstellt'}`
      );
      await load();
      countCtx?.refreshCount?.('giftedBalanceVoucherApprovals');
    } catch (error) {
      if (error?.code === 'GIFTED_VOUCHER_MONTHLY_BUDGET_EXCEEDED') {
        toast.error(monthlyBudgetErrorMessage(error));
      } else {
        toast.error('Fehler beim Freigeben der Anfrage');
      }
    } finally {
      markBusy(requestId, false);
    }
  };

  const openRejectDialog = requestDoc => {
    setRejectDialogRequest(requestDoc);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    setRejectDialogRequest(null);
    setRejectReason('');
  };

  const submitReject = async () => {
    const requestId = rejectDialogRequest?._id;
    if (!requestId) return;

    markBusy(requestId, true);
    try {
      await rejectGiftedBalanceVoucherRequest(requestId, rejectReason.trim());
      toast.success('Anfrage abgelehnt');
      closeRejectDialog();
      await load();
      countCtx?.refreshCount?.('giftedBalanceVoucherApprovals');
    } catch (error) {
      toast.error('Fehler beim Ablehnen der Anfrage');
    } finally {
      markBusy(requestId, false);
    }
  };

  if (!authCtx.checkRight('voucher:special:giftedBalanceApprove')) {
    return <Navigate to='/welcome' replace />;
  }

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: { xs: 2, md: 4 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant='h5' fontWeight='bold'>
          Freigaben: Kulanz-/Lokalmarketing-Gutscheine
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size='small' sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select value={status} label='Status' onChange={e => setStatus(e.target.value)}>
              <MenuItem value='pending'>offen</MenuItem>
              <MenuItem value='approved'>freigegeben</MenuItem>
              <MenuItem value='rejected'>abgelehnt</MenuItem>
              <MenuItem value=''>alle</MenuItem>
            </Select>
          </FormControl>
          <Button variant='outlined' onClick={load} disabled={isLoading}>
            Aktualisieren
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ ...viewRootTextPrimarySx, display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Datum</TableCell>
                <TableCell>Einlösbar in</TableCell>
                <TableCell>Produkt</TableCell>
                <TableCell align='right'>Qty</TableCell>
                <TableCell align='right'>Wert</TableCell>
                <TableCell>Von</TableCell>
                <TableCell>Kommentar</TableCell>
                <TableCell align='right'>Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography color='text.secondary'>Keine Einträge.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                requests.map(r => {
                  const id = r._id;
                  const isBusy = busySet.has(id);
                  const productName = r.relatedVoucherProductId?.name || '-';
                  const branchName =
                    r.branchId?.longName ||
                    r.relatedVoucherProductId?.branchId?.longName ||
                    r.branchId?.locationName ||
                    '-';
                  const entertainmentName =
                    r.entertainmentId?.longName || r.entertainmentId?.entertainmentName || '';
                  const redemptionText =
                    branchName === '-'
                      ? '-'
                      : `${branchName} – ${entertainmentName || 'Alle Entertainments'}`;
                  const requestedAt = r.meta?.requestedAt
                    ? dayjs(r.meta.requestedAt).format('DD.MM.YYYY HH:mm')
                    : '-';
                  const amount = Number(r.amount || 0) || 0;
                  const qty = Number(r.quantity || 1) || 1;

                  const requestComment = r.requestComment || r.reason || '-';
                  const decisionComment = r.decisionComment || '';
                  const commentTitle =
                    r.status === 'rejected' && decisionComment
                      ? `Antrag: ${requestComment}\nAblehnungsgrund: ${decisionComment}`
                      : `Antrag: ${requestComment}`;

                  return (
                    <TableRow key={id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {statusChip(r.status)}
                          {r.budgetExceeded && (
                            <Chip
                              size='small'
                              variant='outlined'
                              color='warning'
                              label='über Kontingent'
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{requestedAt}</TableCell>
                      <TableCell>{redemptionText}</TableCell>
                      <TableCell>{productName}</TableCell>
                      <TableCell align='right'>{qty}</TableCell>
                      <TableCell align='right'>
                        {amount.toLocaleString('de-DE', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{' '}
                        €
                      </TableCell>
                      <TableCell>{formatUser(r.meta?.requestedBy)}</TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 260,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={commentTitle}
                      >
                        {r.status === 'rejected' && decisionComment
                          ? `${requestComment} · Ablehnung: ${decisionComment}`
                          : requestComment}
                      </TableCell>
                      <TableCell align='right'>
                        {r.status === 'pending' ? (
                          <Box sx={{ display: 'inline-flex', gap: 1 }}>
                            <Button
                              size='small'
                              variant='contained'
                              onClick={() => handleApprove(id)}
                              disabled={isBusy}
                            >
                              Freigeben
                            </Button>
                            <Button
                              size='small'
                              variant='outlined'
                              color='error'
                              onClick={() => openRejectDialog(r)}
                              disabled={isBusy}
                            >
                              Ablehnen
                            </Button>
                          </Box>
                        ) : (
                          <Typography color='text.secondary'>-</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={rejectDialogOpen} onClose={closeRejectDialog} maxWidth='sm' fullWidth>
        <AppBar position='static' color='default' elevation={1}>
          <Toolbar>
            <Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
              Anfrage ablehnen
            </Typography>
            <IconButton edge='end' color='inherit' onClick={closeRejectDialog}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant='body2' color='text.secondary'>
            Ablehnungsgrund wird dem Antragsteller angezeigt.
          </Typography>
          <TextField
            label='Ablehnungsgrund'
            placeholder='z.B. Bitte erst Rücksprache mit Standortleitung halten'
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            autoFocus
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button variant='text' onClick={closeRejectDialog}>
              Abbrechen
            </Button>
            <Button variant='contained' color='error' onClick={submitReject}>
              Ablehnen
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}

export default GiftedBalanceVoucherApprovals;

